import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, KeyRound, CreditCard, ShoppingCart, Download, History,
  AlertTriangle, CheckCircle2, Clock, Server, Smartphone, Info, ArrowRight,
} from "lucide-react";

function Card({ icon: Icon, title, children, testid, accent = false }) {
  return (
    <div
      data-testid={testid}
      className={`card-lhs p-5 flex flex-col ${accent ? "border-orange-500/40 orange-glow" : ""}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-orange-500" />
        <h2 className="font-display font-bold uppercase tracking-wider text-sm">{title}</h2>
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function StatRow({ label, value, highlight = false }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-zinc-800/60 last:border-0 text-sm">
      <span className="text-zinc-400">{label}</span>
      <span className={`font-mono-tech ${highlight ? "text-orange-400 font-bold" : "text-white"}`}>{value || "—"}</span>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [appCfg, setAppCfg] = useState(null);
  const [opsCount, setOpsCount] = useState(null);
  const [recentOps, setRecentOps] = useState([]);

  useEffect(() => {
    axios.get(`${API}/app/config`).then((r) => setAppCfg(r.data)).catch(() => {});
    axios.get(`${API}/operations?limit=5`).then((r) => {
      setRecentOps(r.data || []);
    }).catch(() => {});
    axios.get(`${API}/operations?limit=1000`).then((r) => {
      setOpsCount((r.data || []).length);
    }).catch(() => {});
  }, []);

  const isLicActive = user?.license_status === "active";
  const isSuspended = user?.license_status === "suspended";
  const isAdmin = user?.role === "admin";
  const expDate = user?.license_expires_at ? new Date(user.license_expires_at) : null;
  const expDays = expDate ? Math.max(0, Math.ceil((expDate - new Date()) / (1000 * 60 * 60 * 24))) : null;
  const expiringSoon = expDays !== null && expDays <= 7 && expDays > 0;

  const dlUrl = appCfg?.app_download_url
    ? (appCfg.app_download_url.startsWith("http") ? appCfg.app_download_url : `${API}${appCfg.app_download_url.replace(/^\/api/, "")}`)
    : null;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-testid="dashboard-page">
      {/* Welcome header */}
      <div className="flex flex-wrap items-end justify-between gap-4" data-testid="dashboard-header">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold">
            {t("hello", "Bonjour")}, <span className="text-orange-500">{user?.name || "Technicien"}</span>
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Bienvenue dans votre espace LHS Unlocker · {user?.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`brand-pill text-xs ${isLicActive ? "text-emerald-400" : isSuspended ? "text-red-400" : "text-amber-400"}`}>
            <ShieldCheck className="w-3 h-3" />
            {isLicActive ? "LICENCE ACTIVE" : isSuspended ? "COMPTE SUSPENDU" : "LICENCE INACTIVE"}
          </span>
          {isAdmin && <span className="brand-pill text-orange-400 text-xs"><Server className="w-3 h-3" />ADMIN</span>}
        </div>
      </div>

      {/* Important architecture notice (every tech sees this) */}
      <div
        data-testid="desktop-only-banner"
        className="rounded-xl border border-orange-500/30 bg-gradient-to-r from-orange-500/10 to-transparent p-4 flex items-start gap-3"
      >
        <Info className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-bold text-white mb-1">Opérations téléphone : exclusivement via LHS Unlocker Desktop</div>
          <p className="text-zinc-400">
            Toutes les opérations physiques (détection USB, flash, FRP, décodage, lecture info, Hello Bypass…)
            s'effectuent dans l'application <b className="text-orange-400">LHS Unlocker Desktop</b> installée sur votre PC.
            Ce portail web gère uniquement : votre compte, votre licence, vos paiements et vos preuves d'activation.
          </p>
          {dlUrl && (
            <a
              data-testid="dashboard-download-btn"
              href={dlUrl}
              className="inline-flex items-center gap-2 mt-3 bg-orange-500 hover:bg-orange-400 text-black font-bold text-xs px-4 py-2 rounded-lg transition-all"
            >
              <Download className="w-3.5 h-3.5" /> Télécharger LHS Unlocker Desktop v{appCfg?.app_version}
            </a>
          )}
        </div>
      </div>

      {/* Suspended banner */}
      {isSuspended && (
        <div data-testid="dashboard-suspended" className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div className="text-sm text-red-300 flex-1">
            <b>Votre compte est actuellement suspendu.</b> Contactez l'administration pour obtenir de l'assistance.
          </div>
          <button onClick={() => nav("/parametres")} className="text-xs underline text-red-200 hover:text-red-100">
            Mes paramètres
          </button>
        </div>
      )}

      {/* Expiring soon banner */}
      {expiringSoon && !isSuspended && (
        <div data-testid="dashboard-expiring" className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3">
          <Clock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-300 flex-1">
            Votre licence expire dans <b>{expDays} jour{expDays > 1 ? "s" : ""}</b>. Renouvelez dès maintenant pour éviter toute interruption.
          </div>
          <button onClick={() => nav("/paiements")} className="btn-orange text-xs px-3 py-1.5 rounded-lg">Renouveler</button>
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* My license */}
        <Card icon={ShieldCheck} title="Ma licence" testid="card-license" accent={isLicActive}>
          <div className="space-y-1 mb-4">
            <StatRow label="Statut" value={isLicActive ? "✓ ACTIVE" : (user?.license_status || "inactive").toUpperCase()} highlight={isLicActive} />
            <StatRow label="Formule" value={user?.license_plan || "Aucune"} />
            <StatRow label="Expiration" value={expDate ? expDate.toISOString().slice(0, 10) : "—"} />
            <StatRow label="Jours restants" value={expDays !== null ? `${expDays} j` : "—"} highlight={expiringSoon} />
          </div>
          <button
            data-testid="card-license-cta"
            onClick={() => nav(isLicActive ? "/licences" : "/paiements")}
            className={`w-full inline-flex items-center justify-center gap-2 ${isLicActive ? "btn-ghost" : "btn-orange"} text-sm py-2 rounded-lg`}
          >
            {isLicActive ? "Détails de ma licence" : "Activer ma licence"} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </Card>

        {/* My account */}
        <Card icon={KeyRound} title="Mon compte" testid="card-account">
          <div className="space-y-1 mb-4">
            <StatRow label="Nom" value={user?.name} />
            <StatRow label="E-mail" value={user?.email} />
            <StatRow label="Rôle" value={(user?.role || "").toUpperCase()} />
            <StatRow label="Email vérifié" value={user?.email_verified ? "✓ Oui" : "⚠ Non"} />
          </div>
          <button
            data-testid="card-account-cta"
            onClick={() => nav("/parametres")}
            className="w-full btn-ghost text-sm py-2 rounded-lg inline-flex items-center justify-center gap-2"
          >
            Mes paramètres <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </Card>

        {/* Desktop download */}
        <Card icon={Download} title="LHS Unlocker Desktop" testid="card-download">
          <div className="space-y-1 mb-4">
            <StatRow label="Version" value={appCfg?.app_version ? `v${appCfg.app_version}` : "—"} />
            <StatRow label="Taille" value={appCfg?.installer_size_bytes ? `${(appCfg.installer_size_bytes / (1024 * 1024)).toFixed(1)} MB` : "—"} />
            <StatRow label="Compatibilité" value="Windows 7 / 8 / 10 / 11" />
          </div>
          {dlUrl ? (
            <a
              data-testid="card-download-cta"
              href={dlUrl}
              className="w-full btn-orange text-sm py-2 rounded-lg inline-flex items-center justify-center gap-2"
            >
              <Download className="w-3.5 h-3.5" /> Télécharger Setup.exe
            </a>
          ) : (
            <div className="text-xs text-zinc-500 italic">Installateur en préparation…</div>
          )}
        </Card>

        {/* Payments */}
        <Card icon={CreditCard} title="Paiements & abonnements" testid="card-payments">
          <p className="text-sm text-zinc-400 mb-4">
            Choisissez une formule, payez via Airtel Money, Orange Money ou Binance Pay, et obtenez votre preuve d'activation branded.
          </p>
          <button
            data-testid="card-payments-cta"
            onClick={() => nav("/paiements")}
            className="w-full btn-ghost text-sm py-2 rounded-lg inline-flex items-center justify-center gap-2"
          >
            Voir les formules <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </Card>

        {/* Third-party store */}
        <Card icon={ShoppingCart} title="Logiciels tierces" testid="card-store">
          <p className="text-sm text-zinc-400 mb-4">
            Catalogue de 30+ outils complémentaires (UnlockTool, Android Multi Tool, iCloud Bypass, etc.).
            Achat validé manuellement par l'administrateur.
          </p>
          <button
            data-testid="card-store-cta"
            onClick={() => nav("/store")}
            className="w-full btn-ghost text-sm py-2 rounded-lg inline-flex items-center justify-center gap-2"
          >
            Parcourir le catalogue <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </Card>

        {/* History */}
        <Card icon={History} title="Historique récent" testid="card-history">
          {recentOps.length === 0 ? (
            <p className="text-sm text-zinc-500 italic mb-4">
              Aucune opération encore. Les opérations réalisées depuis LHS Unlocker Desktop apparaîtront ici.
            </p>
          ) : (
            <div className="space-y-2 mb-4">
              {recentOps.slice(0, 4).map((op) => (
                <div key={op.id} className="flex items-center gap-2 text-xs">
                  {op.status === "success"
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    : <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                  <span className="text-zinc-400 truncate flex-1">
                    <span className="text-white">{op.operation}</span> · {op.brand} {op.model}
                  </span>
                  <span className="text-zinc-600 font-mono-tech">{(op.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
          <button
            data-testid="card-history-cta"
            onClick={() => nav("/historique")}
            className="w-full btn-ghost text-sm py-2 rounded-lg inline-flex items-center justify-center gap-2"
          >
            Voir tout l'historique ({opsCount ?? "…"}) <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </Card>
      </div>
    </div>
  );
}
